import PropTypes from "prop-types";
import { ProgressCircle } from "@react-spectrum/s2";
import {
  PixelLoader,
  aiLogo,
  image,
  eye,
  mag,
  hourglass,
  wand,
  floppy,
  cloud,
} from "@react-spectrum/ai/loader";

import { VARIATION_PROGRESS_STAGE } from "./wizardSteps";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export { VARIATION_PROGRESS_STAGE };

export const STAGES = {
  [VARIATION_PROGRESS_STAGE.LABELING]: {
    icon: [eye, image, mag, aiLogo],
    label: "Analyzing image",
  },
  [VARIATION_PROGRESS_STAGE.WAITING]: {
    icon: [hourglass, image, aiLogo],
    label: "Generating variation",
  },
  [VARIATION_PROGRESS_STAGE.GENERATING]: {
    icon: [wand, image, aiLogo],
    label: "Generating image",
  },
  [VARIATION_PROGRESS_STAGE.SAVING]: {
    icon: [floppy, image, cloud],
    label: "Saving image",
  },
};

/**
 * Where an image variation is in its generation: an animated loader showing
 * the stage, and — when `showLabel` — what is happening. Only the generating
 * stage has a small circle next to it, determinate once it reports a `value`.
 *
 * `progress` is `{ stage?, value?, label? }`, as kept per variation in the
 * store. Without a `stage` the variation is waiting, and `label` overrides the
 * stage's own (e.g. "Generating video"). `size` is the loader's size in pixels,
 * best as a multiple of 7.
 */
export const VariationProgress = ({
  progress,
  size = 21,
  showLabel = true,
}) => {
  const stageId = STAGES[progress?.stage]
    ? progress.stage
    : VARIATION_PROGRESS_STAGE.WAITING;
  const stage = STAGES[stageId];

  const label = progress?.label ?? stage.label;
  const isLarge = size >= 32;

  return (
    <div
      role="status"
      aria-label={showLabel ? undefined : label}
      className={style({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: { default: 8, isLarge: 16 },
      })({ isLarge })}
    >
      <PixelLoader icon={stage.icon} size={size} />
      {showLabel && (
        <div
          className={style({
            display: "flex",
            alignItems: "center",
            gap: 8,
            position: "relative",
          })}
        >
          {stageId === VARIATION_PROGRESS_STAGE.GENERATING && (
            <ProgressCircle
              size="S"
              staticColor="auto"
              aria-label={label}
              isIndeterminate={!progress?.value}
              value={progress?.value}
              UNSAFE_className={style({
                scale: {
                  default: 0.8,
                  isLarge: 1,
                },
                transformOrigin: "right",
                position: "absolute",
                top: "50%",
                insetStart: -24,
                translateY: "-50%",
              })({ isLarge })}
            />
          )}
          <span
            className={style({
              font: { default: "ui-xs", isLarge: "ui" },
            })({ isLarge })}
          >
            {label}
          </span>
        </div>
      )}
    </div>
  );
};

VariationProgress.propTypes = {
  progress: PropTypes.shape({
    stage: PropTypes.oneOf(Object.values(VARIATION_PROGRESS_STAGE)),
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  size: PropTypes.number,
  showLabel: PropTypes.bool,
};
